/* eslint-disable @typescript-eslint/no-explicit-any */
import { ref, watch, watchEffect, toValue, effectScope, type Ref, type EffectScope } from 'vue'
import { LogicEngine } from 'json-logic-engine-slim'

function registerArrayMembershipMethods(engine: LogicEngine) {
  if (!engine.methods['contains']) {
    engine.addMethod('contains', ([array, item]: [unknown, unknown]) =>
      Array.isArray(array) ? array.includes(item) : false,
    )
  }
  if (!engine.methods['containsAll']) {
    engine.addMethod('containsAll', ([array, items]: [unknown, unknown]) => {
      if (!Array.isArray(array) || !Array.isArray(items)) return false
      return items.every((el) => array.includes(el))
    })
  }
  if (!engine.methods['containsAny']) {
    engine.addMethod('containsAny', ([left, right]: [unknown, unknown]) => {
      const toArr = (v: unknown) => (Array.isArray(v) ? v : typeof v === 'string' ? [v] : null)
      const a = toArr(left)
      const b = toArr(right)
      if (!a || !b) return false
      const set = new Set(a)
      return b.some((el) => set.has(el))
    })
  }
  if (!engine.methods['notContains']) {
    engine.addMethod('notContains', ([array, items]: [unknown, unknown]) => {
      if (!Array.isArray(array)) return false
      const list = Array.isArray(items) ? items : [items]
      return !list.every((el) => array.includes(el))
    })
  }
  if (!engine.methods['optionsOverlap']) {
    engine.addMethod('optionsOverlap', ([left, right]: [unknown, unknown]) => {
      if (!Array.isArray(left) || !Array.isArray(right)) return false
      const rightSet = new Set(right)
      return left.some((el) => rightSet.has(el))
    })
  }
  if (!engine.methods['doesNotContainAny']) {
    engine.addMethod('doesNotContainAny', ([left, right]: [unknown, unknown]) => {
      const toArr = (v: unknown) => (Array.isArray(v) ? v : typeof v === 'string' ? [v] : null)
      const a = toArr(left)
      const b = toArr(right)
      if (!a || !b) return false
      const set = new Set(a)
      return !b.some((el) => set.has(el))
    })
  }
}

const asText = (v: unknown): string | null =>
  typeof v === 'string' ? v : typeof v === 'number' && Number.isFinite(v) ? String(v) : null

// The ingress gateway's rule-set match types (`matchValue` in its rule-set router):
// case-sensitive, and a value that is not set never matches, "does not contain" too.
// Registered unconditionally so an engine release that ships methods of the same name
// cannot change what a stored rule means.
function registerTextMatchMethods(engine: LogicEngine) {
  const define = (name: string, test: (subject: string, pattern: string) => boolean) =>
    engine.addMethod(name, ([subject, pattern]: [unknown, unknown]) => {
      const s = asText(subject)
      const p = asText(pattern)
      return s !== null && p !== null && test(s, p)
    })
  define('startsWith', (s, p) => s.startsWith(p))
  define('endsWith', (s, p) => s.endsWith(p))
  define('containsText', (s, p) => s.includes(p))
  define('notContainsText', (s, p) => !s.includes(p))
}

const toComparableKey = (x: unknown): string =>
  x === null || typeof x !== 'object' ? String(x) : JSON.stringify(x)

function arraysEqualUnordered(a: unknown[], b: unknown[]): boolean {
  if (a.length !== b.length) return false
  if (a.length === 0) return true
  // Multiset equality via sort on stringified keys. Mirrors `==` looseness so
  // [1] == ['1'] holds (same coercion users get with the scalar form today).
  const ka = a.map(toComparableKey).sort()
  const kb = b.map(toComparableKey).sort()
  return ka.every((v, i) => v === kb[i])
}

function lengthIfArray(x: unknown): unknown {
  return Array.isArray(x) ? x.length : x
}

// Overrides the library's built-in `==` `!=` `>` `>=` `<` `<=`. Unconditional
// because the built-ins always pre-exist, so guarding with `engine.methods[op]`
// would block the override.
function registerArrayAwareComparators(engine: LogicEngine) {
  engine.addMethod('==', ([a, b]: [unknown, unknown]) => {
    // Comparing to literal `false` is a "missing or falsy" check — an unset
    // (null) variable, empty string, or 0 all satisfy `var.x == false`.
    if (a === false) return !b
    if (b === false) return !a
    const aIsArr = Array.isArray(a)
    const bIsArr = Array.isArray(b)
    if (aIsArr && bIsArr) return arraysEqualUnordered(a, b)
    if (aIsArr) return arraysEqualUnordered(a, [b])
    if (bIsArr) return arraysEqualUnordered([a], b)
    return a == b
  })
  engine.addMethod('!=', ([a, b]: [unknown, unknown]) => {
    if (a === false) return !!b
    if (b === false) return !!a
    const aIsArr = Array.isArray(a)
    const bIsArr = Array.isArray(b)
    if (aIsArr && bIsArr) return !arraysEqualUnordered(a, b)
    if (aIsArr) return !arraysEqualUnordered(a, [b])
    if (bIsArr) return !arraysEqualUnordered([a], b)
    return a != b
  })
  engine.addMethod(
    '>',
    ([a, b]: [unknown, unknown]) => (lengthIfArray(a) as any) > (lengthIfArray(b) as any),
  )
  engine.addMethod(
    '>=',
    ([a, b]: [unknown, unknown]) => (lengthIfArray(a) as any) >= (lengthIfArray(b) as any),
  )
  engine.addMethod(
    '<',
    ([a, b]: [unknown, unknown]) => (lengthIfArray(a) as any) < (lengthIfArray(b) as any),
  )
  engine.addMethod(
    '<=',
    ([a, b]: [unknown, unknown]) => (lengthIfArray(a) as any) <= (lengthIfArray(b) as any),
  )
}

// 1. Recursive dependency extractor
function extractDependencies(rule: any, opName = 'field'): Set<string> {
  const keys = new Set<string>()
  function traverse(node: any) {
    if (!node || typeof node !== 'object') return

    if (node[opName]) {
      const arg = Array.isArray(node[opName]) ? node[opName][0] : node[opName]
      if (typeof arg === 'string') {
        keys.add(arg)
      }
    }

    Object.values(node).forEach((child) => {
      if (Array.isArray(child)) {
        child.forEach(traverse)
      } else {
        traverse(child)
      }
    })
  }
  traverse(rule)
  return keys
}

export function useSmartRule(
  rule: Ref<any> | any,
  getData: (key: string) => unknown,
  engine?: LogicEngine,
) {
  const result = ref<any>(null)
  const isLoading = ref(false)
  const localEngine = engine || new LogicEngine()

  if (!localEngine.methods['field']) {
    localEngine.addMethod('field', (...args) => {
      // args[0] contains the operands. It might be a string or an array of strings.
      const firstArg = args[0]
      // Extract the actual string key
      const key = Array.isArray(firstArg) ? firstArg[0] : firstArg
      // Safety check to prevent the TypeError in getData
      if (typeof key !== 'string') {
        console.warn('[LogicEngine] "field" expected a string key, but got:', key)
        return null
      }

      const value = getData(key)
      if (value === undefined) return null
      return value
    })
  }

  registerArrayMembershipMethods(localEngine)
  registerArrayAwareComparators(localEngine)
  registerTextMatchMethods(localEngine)

  // Never let the failure log itself throw. `rule` may be a Ref/computed
  // (DiscountCode passes one), and JSON.stringify rejects those as a circular
  // structure. With a synchronous `execute` that throw would escape the
  // watcher job and, in a dev build, be re-thrown by Vue's logError — killing
  // the calling setup() instead of logging a bad rule.
  function logRuleFailure(e: unknown) {
    try {
      console.error('[Rule] Execution failed', e, JSON.stringify(toValue(rule)))
    } catch {
      console.error('[Rule] Execution failed', e)
    }
  }

  // SYNCHRONOUS ON PURPOSE — do not reintroduce `await`. `LogicEngine.run` is
  // fully synchronous, and under SSR / `nuxt generate` prerender Vue runs an
  // immediate watcher exactly once and then STOPS it (runtime-core `doWatch`,
  // isInSSRComponentSetup branch). Deferring this assignment by even one
  // microtask leaves `result` null for the whole server render, so every ruled
  // node evaluates false in `useVisibilityMap` and is missing from the static
  // HTML — it only appears after hydration. The thenable branch is an escape
  // hatch in case an async method is ever registered on the engine.
  const execute = () => {
    try {
      const value = localEngine.run(toValue(rule))
      if (value && typeof (value as { then?: unknown }).then === 'function') {
        isLoading.value = true
        ;(value as Promise<unknown>)
          .then((resolved) => {
            result.value = resolved
          })
          .catch((e) => logRuleFailure(e))
          .finally(() => {
            isLoading.value = false
          })
        return
      }
      result.value = value
    } catch (e) {
      logRuleFailure(e)
    }
  }

  let unwatchRules: ReturnType<typeof watch> | null = null

  // 2. The "Smart" Watcher System
  watch(
    () => toValue(rule),
    (newRule) => {
      if (unwatchRules) {
        unwatchRules()
        unwatchRules = null
      }

      if (!newRule) return

      const dependencies = extractDependencies(newRule, 'field')

      if (dependencies.size === 0) {
        execute()
        return
      }

      // B. Create targeted getters for specific paths using the provided function
      const watchers = Array.from(dependencies).map((path) => {
        return () => getData(path) // <-- The watcher evaluates the getter
      })

      // C. Watch the specific paths
      unwatchRules = watch(watchers, execute, { immediate: true })
    },
    { immediate: true, deep: true },
  )

  return { result, isLoading }
}

/**
 * One-shot evaluation for gating event processors. Fail-open: on error or
 * empty rule returns true so the processor still runs — matches useSmartRule's
 * catch-and-log behaviour. A module-level engine is cached and the active
 * getData is swapped in via a ref, so the 'field' method stays registered
 * once while routing to the correct pipe's data per invocation.
 */
let oneShotEngine: LogicEngine | null = null
let oneShotGetData: ((key: string) => unknown) | null = null
export async function evalRule(rule: unknown, getData: (key: string) => unknown): Promise<boolean> {
  if (!rule || typeof rule !== 'object' || Object.keys(rule as object).length === 0) {
    return true
  }
  if (!oneShotEngine) {
    oneShotEngine = new LogicEngine()
    oneShotEngine.addMethod('field', (...args: any[]) => {
      const firstArg = args[0]
      const key = Array.isArray(firstArg) ? firstArg[0] : firstArg
      if (typeof key !== 'string') return null
      const v = oneShotGetData?.(key)
      return v === undefined ? null : v
    })
    registerArrayMembershipMethods(oneShotEngine)
    registerArrayAwareComparators(oneShotEngine)
    registerTextMatchMethods(oneShotEngine)
  }
  try {
    oneShotGetData = getData
    return !!(await oneShotEngine.run(rule))
  } catch (e) {
    console.error('[Rule] processor rule eval failed', e, JSON.stringify(rule))
    return true
  } finally {
    oneShotGetData = null
  }
}

/**
 * useVisibilityMap
 * Passes the getter down to useSmartRule.
 */
export function useVisibilityMap(
  schemaSource: Ref<any> | any,
  getData: (key: string) => unknown, // <-- Updated signature
) {
  const visibilityMap = ref<Record<string, boolean>>({})
  let scope: EffectScope | null = null

  watch(
    () => toValue(schemaSource),
    (newSchema) => {
      if (scope) scope.stop()
      scope = effectScope()

      visibilityMap.value = {}
      if (!newSchema?.children) return

      scope.run(() => {
        newSchema.children.forEach((node: any) => {
          if (!node.rules || Object.keys(node.rules).length === 0) {
            visibilityMap.value[node.nodeId] = true
            return
          }

          visibilityMap.value[node.nodeId] = false
          const { result } = useSmartRule(node.rules, getData)

          watchEffect(() => {
            visibilityMap.value[node.nodeId] = !!result.value
          })
        })
      })
    },
    { immediate: true },
  )

  return visibilityMap
}
